import type React from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { SignatureSocketWriteOperationPayload } from '../../providers/SignaturesProvider';
import { useDrag } from 'react-dnd';
import { getSignatureColor, SignatureBaseButton } from '../../shared/components/SignatureBaseButton';
import { getDefaultSignature, getDefaultSignatureDimensions } from '../../signatures/constants';
import type { AssignedSignee } from 'services/entities/SignatureTypes';
import { GridBlockType } from 'services/entities/BlockTypes';
import { useClickToAddSignature } from '../../hooks/useClickToAddSignature';
import { draggableSignatureButtonTestIds } from './DraggableSignatureButton.test-ids';

interface SignatureButtonProps {
  signee?: AssignedSignee | null;
}

export const DraggableSignatureButton: React.FC<SignatureButtonProps> = ({ signee }) => {
  const { t } = useTranslation();
  const clickToAddSignature = useClickToAddSignature();

  const wasDraggingRef = useRef(false);
  const defaultSignature = getDefaultSignature();
  const item: SignatureSocketWriteOperationPayload = {
    ...defaultSignature,
  };
  if (signee) {
    item.assignedSignee = signee;
  }

  const [{ isDragging }, drag] = useDrag({
    type: GridBlockType.SIGNATURE,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    item: () => {
      wasDraggingRef.current = true;
      return item;
    },
    end: () => {
      requestAnimationFrame(() => {
        wasDraggingRef.current = false;
      });
    },
  });

  const handleClick = () => {
    if (wasDraggingRef.current) {
      return;
    }
    clickToAddSignature(signee ?? null);
  };

  const defaultSignatureDimensions = getDefaultSignatureDimensions();

  return (
    <SignatureBaseButton
      ref={drag}
      data-testid={draggableSignatureButtonTestIds.button}
      style={{
        width: defaultSignatureDimensions.width,
        height: defaultSignatureDimensions.height,
        opacity: isDragging ? 0.5 : 1,
      }}
      draggable={true}
      onClick={handleClick}
      color={getSignatureColor(signee?.email)}
      title={signee?.email ?? t('unassigned')}
    >
      Signature
    </SignatureBaseButton>
  );
};
